import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserCheck,
  UserPlus,
  Globe,
  Building2,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Settings,
  DollarSign,
  ArrowUpRight,
} from 'lucide-react';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { useStore } from '../hooks/useStore';

/**
 * Panoramica (home dashboard admin) — rifacimento grafico "dark sidebar".
 *
 * Tutti i grafici sono alimentati da dati REALI dello snapshot
 * GET /api/admin/dashboard/stats (DashboardStats): contatori utenti per ruolo,
 * registrazioni mensili, lingue, progresso traduzioni. Nessun dato inventato:
 * non essendoci una serie storica su questo endpoint, non mostriamo grafici
 * temporali qui (l'andamento vive nelle pagine Statistiche/Abbonamenti).
 */

const NIVO_THEME = {
  text: { fontFamily: 'Inter, sans-serif', fontSize: 12, fill: '#64748b' },
  axis: {
    ticks: { text: { fill: '#94a3b8', fontSize: 11 } },
    domain: { line: { stroke: '#e2e8f0' } },
  },
  grid: { line: { stroke: '#eef1f6', strokeWidth: 1 } },
  tooltip: {
    container: {
      background: '#0f1426',
      color: '#fff',
      fontSize: 12,
      borderRadius: 8,
      padding: '6px 10px',
    },
  },
};

const Dashboard: React.FC = () => {
  const { stats, isLoadingStats, loadDashboardStats } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardStats();
  }, []);

  if (isLoadingStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    );
  }

  // ── Dati reali per i grafici ──────────────────────────────────────────────
  const roles = [
    { id: 'Business Owner', short: 'Business', value: stats?.totalBusinessOwners || 0, color: '#6366f1' },
    { id: 'Influencer', short: 'Influencer', value: stats?.totalInfluencers || 0, color: '#ec4899' },
    { id: 'Ambassador', short: 'Ambassador', value: stats?.totalAmbassadors || 0, color: '#f97316' },
    { id: 'Seller', short: 'Seller', value: stats?.totalSellers || 0, color: '#a855f7' },
  ];
  const roleSum = roles.reduce((acc, r) => acc + r.value, 0);

  const barData = roles.map((r) => ({ role: r.short, value: r.value, color: r.color }));
  const pieData = roles.map((r) => ({ id: r.id, label: r.id, value: r.value, color: r.color }));

  const translationPct =
    stats?.completedTranslations && stats?.totalTranslations
      ? Math.round((stats.completedTranslations / stats.totalTranslations) * 100)
      : 0;
  const pendingTranslations = (stats?.totalTranslations || 0) - (stats?.completedTranslations || 0);

  const kpis = [
    { name: 'Utenti totali', value: stats?.totalUsers || 0, icon: Users, tint: 'text-indigo-500' },
    { name: 'Utenti attivi', value: stats?.activeUsers || 0, icon: UserCheck, tint: 'text-emerald-500' },
    { name: 'Registrazioni mensili', value: stats?.monthlyRegistrations || 0, icon: UserPlus, tint: 'text-violet-500' },
    { name: 'Lingue attive', value: stats?.enabledLanguages || 0, icon: Globe, tint: 'text-amber-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[26px] font-extrabold text-slate-900 tracking-tight">Panoramica piattaforma</h2>
          <p className="text-sm text-slate-500">Snapshot in tempo reale di utenti, ruoli e traduzioni.</p>
        </div>
        <div className="flex items-center gap-2 h-10 px-4 rounded-lg bg-white border border-slate-200 text-sm font-medium text-slate-600">
          <Calendar className="h-4 w-4 text-indigo-500" />
          Aggiornato adesso
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div
              key={k.name}
              className="card-glow bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm"
            >
              <p className="text-sm text-slate-500 flex items-center gap-2">
                <Icon className={`h-4 w-4 ${k.tint}`} />
                {k.name}
              </p>
              <p className="text-3xl font-extrabold text-slate-900 mt-3">
                {k.value.toLocaleString('it-IT')}
              </p>
            </div>
          );
        })}
      </div>

      {/* Grafici */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Bar — utenti per ruolo */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm lg:col-span-2">
          <h3 className="font-bold text-slate-900 mb-1">Utenti per ruolo</h3>
          <p className="text-xs text-slate-400 mb-2">Distribuzione assoluta degli account per tipologia.</p>
          <div className="h-72">
            <ResponsiveBar
              data={barData as any}
              keys={['value']}
              indexBy="role"
              theme={NIVO_THEME as any}
              margin={{ top: 12, right: 8, bottom: 36, left: 44 }}
              padding={0.45}
              colors={(bar: any) => bar.data.color}
              borderRadius={6}
              enableLabel={false}
              axisLeft={{ tickSize: 0, tickPadding: 10 }}
              axisBottom={{ tickSize: 0, tickPadding: 10 }}
              gridYValues={4}
              animate
              motionConfig="gentle"
            />
          </div>
        </div>

        {/* Donut — distribuzione ruoli */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Distribuzione ruoli</h3>
          <div className="relative h-44">
            <ResponsivePie
              data={pieData as any}
              theme={NIVO_THEME as any}
              margin={{ top: 6, right: 6, bottom: 6, left: 6 }}
              innerRadius={0.72}
              padAngle={1.5}
              cornerRadius={4}
              colors={{ datum: 'data.color' }}
              borderWidth={0}
              enableArcLabels={false}
              enableArcLinkLabels={false}
              activeOuterRadiusOffset={6}
              animate
              motionConfig="gentle"
            />
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                <p className="text-2xl font-extrabold text-slate-900 leading-none">
                  {roleSum.toLocaleString('it-IT')}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">profili</p>
              </div>
            </div>
          </div>
          <div className="space-y-2 mt-4 text-sm">
            {roles.map((r) => (
              <div key={r.id} className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-slate-600">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.color }} />
                  {r.id}
                </span>
                <span className="font-semibold text-slate-800">{r.value.toLocaleString('it-IT')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Azioni rapide */}
      <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm">
        <h3 className="font-bold text-slate-900 mb-4">Azioni rapide</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickAction title="Gestisci utenti" desc="Crea e gestisci gli account" icon={Users} onClick={() => navigate('/users')} />
          <QuickAction title="Configurazione" desc="Dati piattaforma e impostazioni" icon={Settings} onClick={() => navigate('/config')} />
          <QuickAction title="Lingue" desc="Lingue e traduzioni" icon={Globe} onClick={() => navigate('/languages')} />
          <QuickAction title="Commissioni" desc="Regole e tassi di commissione" icon={DollarSign} onClick={() => navigate('/commissions')} />
        </div>
      </div>

      {/* Progresso traduzioni + Stato sistema */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Progresso traduzioni</h3>
          <div className="flex items-end justify-between mb-2">
            <span className="text-3xl font-extrabold text-slate-900">{translationPct}%</span>
            <span className="text-xs text-slate-400">
              {(stats?.completedTranslations || 0).toLocaleString('it-IT')}/
              {(stats?.totalTranslations || 0).toLocaleString('it-IT')}
            </span>
          </div>
          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all"
              style={{ width: `${translationPct}%` }}
            />
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-sm">
            <span className="text-slate-500 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-indigo-500" />
              Business Owner
            </span>
            <span className="font-bold text-slate-900">
              {(stats?.totalBusinessOwners || 0).toLocaleString('it-IT')}
            </span>
          </div>
        </div>

        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm lg:col-span-2">
          <h3 className="font-bold text-slate-900 mb-4">Stato sistema</h3>
          <div className="space-y-2.5">
            <StatusItem
              label="Connessione database"
              status="healthy"
              description="Operazioni sul database regolari"
            />
            <StatusItem
              label="Servizi API"
              status="healthy"
              description="Tutti gli endpoint rispondono correttamente"
            />
            <StatusItem
              label="Sistema traduzioni"
              status={pendingTranslations > 0 ? 'warning' : 'healthy'}
              description={
                pendingTranslations > 0
                  ? `${pendingTranslations.toLocaleString('it-IT')} traduzioni da completare`
                  : 'Tutte le traduzioni sono complete'
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Sottocomponenti ─────────────────────────────────────────────────────────

interface QuickActionProps {
  title: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}

const QuickAction: React.FC<QuickActionProps> = ({ title, desc, icon: Icon, onClick }) => (
  <button
    onClick={onClick}
    className="group text-left p-4 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
  >
    <div className="flex items-center justify-between">
      <span className="h-9 w-9 grid place-items-center rounded-lg bg-indigo-50 text-indigo-600">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-500 transition-colors" />
    </div>
    <p className="mt-3 text-sm font-semibold text-slate-800">{title}</p>
    <p className="text-xs text-slate-500">{desc}</p>
  </button>
);

interface StatusItemProps {
  label: string;
  status: 'healthy' | 'warning' | 'error';
  description: string;
}

const StatusItem: React.FC<StatusItemProps> = ({ label, status, description }) => {
  const cfg = {
    healthy: { Icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    warning: { Icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50' },
    error: { Icon: AlertTriangle, color: 'text-rose-500', bg: 'bg-rose-50' },
  }[status];
  const Icon = cfg.Icon;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className={`${cfg.bg} p-2 rounded-lg`}>
        <Icon className={`h-4 w-4 ${cfg.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">{label}</p>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
    </div>
  );
};

export default Dashboard;
